import type { ReactNode } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import type { SortDir } from '../hooks/useSortable.js';

interface SortHeaderProps {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: 'left' | 'right' | 'center';
  /** Optional extra classes appended AFTER the defaults — use this to
   *  override the default `px-3` padding on specific columns. */
  className?: string;
  /** Optional control rendered beside the label (e.g. a column filter funnel).
   *  It owns its own clicks (stopPropagation) so it doesn't trigger the sort. */
  filter?: ReactNode;
  /** Full/unabbreviated label shown on hover (e.g. "UoM status" while the column
   *  header reads "UOM"). Defaults to `label`. */
  title?: string;
}

export function SortHeader({ label, active, dir, onClick, align = 'left', className = '', filter, title }: SortHeaderProps) {
  return (
    <th
      className={`px-3 py-3 ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'} cursor-pointer select-none transition-colors hover:bg-accent/40 ${className}`}
      onClick={onClick}
      title={title ?? label}
    >
      {/* Flex row where ONLY the label can shrink/ellipsis: the sort arrow and the
          filter funnel are shrink-0 (the funnel via ColumnFilter's own shrink-0), so
          they're always reserved + visible no matter how narrow the column or what the
          zoom is. The label `truncate` clips itself (NOT the <th> — truncate on a
          table-cell is unreliable); the th's overflow-hidden is just a backstop. */}
      <span className={`eyebrow flex min-h-[1rem] w-full items-center gap-1 ${align === 'right' ? 'justify-end' : align === 'center' ? 'justify-center' : ''} ${active ? 'text-primary' : ''}`}>
        <span className="min-w-0 truncate">{label}</span>
        {active ? (
          dir === 'asc' ? (
            <ArrowUp className="h-3 w-3 shrink-0" />
          ) : (
            <ArrowDown className="h-3 w-3 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 shrink-0 opacity-30" />
        )}
        {filter}
      </span>
    </th>
  );
}
